import { useState, useEffect } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { aiChatAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Users, Clock, MessageSquare, Calendar, Mail, Sparkles,
  Loader2, Play, CheckCircle2, AlertTriangle, ChevronRight, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = { Users, Clock, MessageSquare, Calendar, Mail, Sparkles };

export default function AIAgentsPage() {
  const { currentWorkspace } = useWorkspace();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState({});
  const [results, setResults] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const wsId = currentWorkspace?.id;

  useEffect(() => {
    if (!wsId) return;
    aiChatAPI.agents(wsId).then(res => { setAgents(res.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, [wsId]);

  const toggleAgent = async (agentId, enabled) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/agents/${wsId}/${agentId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('stellar_token')}` },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error();
      setAgents(prev => prev.map(a => a.id === agentId ? { ...a, status: enabled ? 'active' : 'inactive' } : a));
      toast.success(enabled ? 'Automation turned ON' : 'Automation turned OFF');
    } catch { toast.error('Failed to toggle'); }
  };

  const runAgent = async (agentId) => {
    setRunning(prev => ({ ...prev, [agentId]: true }));
    setResults(prev => ({ ...prev, [agentId]: null }));
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/agents/${wsId}/${agentId}/run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('stellar_token')}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      setResults(prev => ({ ...prev, [agentId]: data }));
      toast.success(data.message || 'Automation completed');
    } catch { toast.error('Automation failed'); }
    setRunning(prev => ({ ...prev, [agentId]: false }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-gold/40" /></div>;

  const simple = agents.filter(a => !a.advanced);
  const advanced = agents.filter(a => a.advanced);

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8" data-testid="ai-agents-page">
      <div className="mb-8">
        <h1 className="text-xl font-display font-bold">Automations</h1>
        <p className="text-[13px] text-muted-foreground/50 mt-1">Two simple switches that help your team. ON means it works automatically — nothing to configure.</p>
      </div>

      <div className="grid gap-4">
        {simple.map(agent => {
          const Icon = ICON_MAP[agent.icon] || Sparkles;
          const isActive = agent.status === 'active';
          const isRunning = running[agent.id];
          const result = results[agent.id];
          return (
            <Card key={agent.id} className={`bg-card/60 backdrop-blur border-border/30 transition-all ${isActive ? 'border-gold/25' : ''}`} data-testid={`agent-${agent.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${isActive ? 'bg-gold/10' : 'bg-foreground/[0.03]'}`}>
                    <Icon className={`h-5 w-5 ${isActive ? 'text-gold' : 'text-muted-foreground/40'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-[14px] font-semibold">{agent.name}</h3>
                      <Badge variant="outline" className={`text-[9px] ${isActive ? 'border-gold/30 text-gold' : 'border-border/30 text-muted-foreground/40'}`} data-testid={`agent-${agent.id}-status`}>
                        {isActive ? 'ON' : 'OFF'}
                      </Badge>
                    </div>
                    <p className="text-[12px] text-muted-foreground/60">{agent.desc}</p>
                    {result && (
                      <div className={`mt-3 p-3 rounded-lg text-[12px] ${result.detail || result.message?.includes('error') ? 'bg-red-500/5 border border-red-500/20 text-red-400' : 'bg-gold/5 border border-gold/15 text-foreground/70'}`}>
                        <div className="flex items-center gap-1.5">
                          {result.detail || result.message?.includes('error') ? <AlertTriangle className="h-3.5 w-3.5 text-red-400" /> : <CheckCircle2 className="h-3.5 w-3.5 text-gold" />}
                          <span className="font-medium">{result.message || result.detail}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Button variant="outline" size="sm" className="h-8 text-[11px] gap-1.5 border-border/30" onClick={() => runAgent(agent.id)} disabled={isRunning} data-testid={`run-${agent.id}`}>
                      {isRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Run
                    </Button>
                    <Switch checked={isActive} onCheckedChange={(v) => toggleAgent(agent.id, v)} data-testid={`toggle-${agent.id}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {advanced.length > 0 && (
        <div className="mt-10">
          <button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            className="flex items-center gap-1.5 text-[12px] text-muted-foreground/50 hover:text-muted-foreground/80 transition-colors"
            data-testid="advanced-automations-toggle"
          >
            {showAdvanced ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Advanced (disabled) — for future use, not active
          </button>
          {showAdvanced && (
            <div className="grid gap-3 mt-3 opacity-50 pointer-events-none select-none" data-testid="advanced-automations-list">
              {advanced.map(agent => {
                const Icon = ICON_MAP[agent.icon] || Sparkles;
                return (
                  <Card key={agent.id} className="bg-card/40 border-border/20" data-testid={`agent-${agent.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 bg-foreground/[0.03]">
                          <Icon className="h-4 w-4 text-muted-foreground/40" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-[13px] font-medium text-foreground/70">{agent.name}</h3>
                            <Badge variant="outline" className="text-[9px] border-border/30 text-muted-foreground/40">Not active</Badge>
                          </div>
                          <p className="text-[11px] text-muted-foreground/40 mt-0.5">{agent.desc}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
